import React, { useContext, useRef } from "react";
import { Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../starek.css";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContextProvider,
    SidebarContext,
    useSyncedField,
} from "../../components/Sidebar";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE_MB = 2;

function PartImageUpload() {
    const [imageDataUrl, setImageDataUrl] = useSyncedField("image_data_url", "");
    const [, setImageFile] = useSyncedField("image_file", null);
    const { entity } = useContext(SidebarContext);
    const inputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            alert("Only JPEG, PNG, WebP, or GIF images are allowed.");
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            alert(`Image must be smaller than ${MAX_IMAGE_SIZE_MB}MB.`);
            return;
        }

        setImageFile(file);

        const reader = new FileReader();
        reader.onload = (ev) => setImageDataUrl(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemove = () => {
        setImageDataUrl("");
        setImageFile(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const existingUrl = entity?.image_asset?.url;
    const previewSrc = imageDataUrl || existingUrl;

    return (
        <Form.Group className="mb-3" controlId="part_image">
            {/* <Form.Label className="d-block text-center">
                Image <span className="text-muted" style={{ fontSize: "0.8rem" }}>(simulation)</span>
            </Form.Label> */}

            <div className="d-flex flex-column align-items-center gap-2">
                {previewSrc ? (
                    <>
                        <img
                            src={previewSrc}
                            alt="Part preview"
                            style={{
                                width: 120,
                                height: 120,
                                objectFit: "cover",
                                borderRadius: "50%",
                                border: "2px solid #dee2e6",
                            }}
                        />
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => inputRef.current?.click()}
                            >
                                <i className="fa fa-pencil me-1" /> Change
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={handleRemove}
                            >
                                <i className="fa fa-trash me-1" /> Remove
                            </button>
                        </div>
                    </>
                ) : (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center border"
                        style={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            cursor: "pointer",
                            background: "#f8f9fa",
                            color: "#6c757d",
                        }}
                        onClick={() => inputRef.current?.click()}
                        role="button"
                        aria-label="Upload part image"
                    >
                        <i className="fa fa-camera fa-2x mb-1" />
                        <small>Upload</small>
                    </div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </Form.Group>
    );
}

function PartBasicDetails() {
    const [name, setName] = useSyncedField("name", "");
    const [description, setDescription] = useSyncedField("description", "");

    return (
        <>
            <PartImageUpload />
            <Form.Group className="mb-3" controlId="part_name">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    name="part_name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Form.Group>
            <Form.Group className="mb-3" controlId="part_description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Max allowed characters: 120"
                    name="part_description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </Form.Group>
        </>
    );
}

function PartSidebar({ show, item, plantId, onDiscard, onSave, onDelete, onToggleActive }) {
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            if (!emittedData.name?.trim()) {
                starekContext.showError("Part name is required");
                throw new Error("Part name is required");
            }

            const payload = {
                id: entity?.id,
                name: emittedData.name.trim(),
                description: emittedData.description || "",
                plant_id: plantId,
                image_file: emittedData.image_file || null,
            };

            await onSave(payload);
        } catch (error) {
            console.error("Error saving part:", error);
            starekContext.showError(
                error?.message || "Failed to save part. Please try again.",
            );
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? async (entity) => await onDelete(entity) : null}
            onDiscard={async () => await onDiscard()}
            onToggleActive={onToggleActive}
        >
            <SidebarView entityName="Part">
                <SidebarView.Page
                    key={item?.id || "new"}
                    name="basic_details"
                    title="Basic Details"
                >
                    <PartBasicDetails />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default PartSidebar;
